/**
 * cro-conversion.js  v3.0 — CRO Conversion Controller
 * ─────────────────────────────────────────────────────────────────────────────
 * Bilingual (AR/EN) urgency, scarcity & social-proof system for IDEA theme.
 *
 * v3.0 — Coupon layer removed.
 * Prices are now discounted directly on the products themselves (tiered
 * EGP 500 / 800 / 1000 off, applied to the variant price with the original
 * kept as compare-at). The theme therefore no longer advertises any code:
 * the winner popup, the sticky announcement bar and the product discount
 * alert have all been deleted, along with their strings, styles and markup.
 * What remains is scarcity + social proof only — no price claims.
 *
 * STANDING NOTES
 * ──────────────
 * #1 — Buttons overlap (LTR): inject as block siblings of .idea-product__atc-row,
 *      never as children inside the flex container.
 * #2 — Design clashes: ZERO hardcoded colours; all values from cro-theme-adapter.css.
 * #3 — Color mismatch: covered by theme-adapter (see that file).
 * #4 — Auto-activation: all components fire immediately on DOMContentLoaded.
 * #5 — Missing AR strings: complete i18n dictionary for every visible string.
 *
 * ELIGIBILITY GATE
 * ─────────────────
 *   DATA.active === true  (chair product AND price < 500,000 cents)
 *   JS confirms: priceCents < 500000 (strict)
 *   If not met: zero DOM mutations, zero CSS loaded.
 *
 * ARCHITECTURE
 * ─────────────
 *   § 1  Bootstrap      parse blob, dual eligibility check, locale detection
 *   § 2  i18n           bilingual string dictionary for every UI string
 *   § 3  DOM refs       element handles gathered once at boot
 *   § 4  Utilities      show / hide / pad2
 *   § 5  SmartStock     real inventory badge before .idea-product__atc-row
 *   § 6  UrgencyText    block sibling after .idea-product__atc-row
 *   § 7  CartTimer      60-min reservation countdown (localStorage)
 *   § 8  CartInterceptor fetch + XHR + custom-event patches
 *   § 9  SocialProof    rotating toast notifications (45–90 s)
 *   § 10 Init           boots all modules after DOM ready
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function CROController() {
  'use strict';

  /* ══════════════════════════════════════════════════════════════
     § 1  BOOTSTRAP
     ══════════════════════════════════════════════════════════════ */

  const dataEl = document.getElementById('cro-data-layer');
  if (!dataEl) return;

  let DATA;
  try { DATA = JSON.parse(dataEl.textContent); }
  catch (_) { return; }

  // ── Dual eligibility check (Liquid + JS) ──────────────────────
  // Guardrail: price must be strictly less than EGP 5,000.
  const CRO_PRICE_LIMIT = 500000;
  if (!DATA.active) return;
  if (DATA.price > CRO_PRICE_LIMIT) return; // defensive JS-side re-check (inclusive boundary)

  window.__CRO_DATA = DATA;

  // ── Locale — BUG #4 FIX: multi-source, NO window.location read ──
  // Priority: data layer → html[lang] → theme global → fallback 'en'
  const _rawLang = (
    DATA.lang ||
    document.documentElement.lang ||
    (window.IDEA && window.IDEA.locale) ||
    'en'
  ).slice(0, 2).toLowerCase();

  const isAR = _rawLang === 'ar';

  /* ══════════════════════════════════════════════════════════════
     § 2  i18n DICTIONARY  — BUG #7 FIX: complete AR/EN strings
     ══════════════════════════════════════════════════════════════ */

  const T = {
    stock: {
      critical: isAR ? '🔥 آخر قطعتين فقط'  : '🔥 Only 2 left',
      low:      isAR ? '⚠️ الكمية محدودة'    : '⚠️ Low stock',
      ok:       isAR ? '✔️ متوفر الآن'       : '✔️ In stock',
      demand:   isAR
        ? '⚠️ هذا الكرسي عليه طلب مرتفع الآن وقد ينفذ قريبًا'
        : '⚠️ High demand on this chair — may run out soon',
    },

    urgency: isAR
      ? '⏳ العرض سينتهي قريبًا — احجز خصمك الآن قبل انتهاء الكمية'
      : '⏳ Offer ending soon — secure your discount before stock runs out',

    reservation: {
      msg:   isAR
        ? '🛒 تم حجز هذا الكرسي لك لمدة 60 دقيقة فقط'
        : '🛒 This chair is reserved for you for 60 minutes',
      label: isAR ? '⏳ ينتهي الحجز خلال: ' : '⏳ Reservation expires in: ',
    },

    toasts: isAR
      ? [
          '👤 عميل اشترى هذا الكرسي منذ دقائق',
          '🔥 هذا الكرسي يتم طلبه الآن بكثرة',
        ]
      : [
          '👤 Someone purchased this chair a few minutes ago',
          '🔥 This chair is trending right now',
        ],

    // BUG #7 FIX: aria-labels also translated
    aria: {
      reserveTimer: isAR ? 'مؤقت الحجز'      : 'Reservation countdown',
    },
  };

  /* ══════════════════════════════════════════════════════════════
     § 3  DOM REFERENCES — gathered once; checked before every use
     ══════════════════════════════════════════════════════════════ */

  const $id = (id)       => document.getElementById(id);
  const $qs = (sel, ctx) => (ctx || document).querySelector(sel);

  const el = {
    // Inline
    stock:       $id('cro-stock'),
    stockLevel:  $qs('#cro-stock .cro-stock__level'),
    stockDemand: $qs('#cro-stock .cro-stock__demand'),
    urgency:     $id('cro-urgency'),
    // Cart
    reservation: $id('cro-reservation'),
    resMsg:      $qs('#cro-reservation .cro-reservation__msg'),
    resTimer:    $qs('#cro-reservation .cro-reservation__timer'),
    // Toast
    toast:       $id('cro-toast'),
  };

  /* ══════════════════════════════════════════════════════════════
     § 4  UTILITIES
     ══════════════════════════════════════════════════════════════ */

  const show = (e) => { if (e) e.removeAttribute('hidden'); };
  const hide = (e) => { if (e) e.setAttribute('hidden', ''); };
  const pad2 = (n) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

  /* ══════════════════════════════════════════════════════════════
     § 5  SMART STOCK
     BUG #1 FIX: inserted BEFORE .idea-product__atc-row (the flex
     container), never inside it as a flex item.
     Stock numbers = real Shopify inventory — zero fake data.
     ══════════════════════════════════════════════════════════════ */

  function initStock() {
    if (!el.stock || !el.stockLevel) return;
    if (DATA.inventoryTracked === false) return;

    const qty = DATA.inventory;

    let text, color;
    if (qty <= 2) {
      text  = T.stock.critical;
      color = 'var(--cro-scarcity)';
    } else if (qty <= 5) {
      text  = T.stock.low;
      color = 'var(--cro-urgency)';
    } else {
      text  = T.stock.ok;
      color = 'var(--cro-success)';
    }

    el.stockLevel.textContent = text;
    el.stockLevel.style.color = color;

    if (qty <= 5 && el.stockDemand) {
      el.stockDemand.textContent = T.stock.demand;
      show(el.stockDemand);
    }

    // BUG #1 FIX: target the ROW, not a button inside it
    const atcRow = $qs('.idea-product__atc-row');
    if (atcRow?.parentNode) {
      atcRow.parentNode.insertBefore(el.stock, atcRow);
    }

    show(el.stock);
  }

  /* ══════════════════════════════════════════════════════════════
     § 6  URGENCY TEXT
     BUG #1 FIX: inserted as block sibling AFTER .idea-product__atc-row.
     BUG #7 FIX: text from T dictionary.
     ══════════════════════════════════════════════════════════════ */

  function initUrgency() {
    if (!el.urgency) return;

    el.urgency.textContent = T.urgency;

    const atcRow = $qs('.idea-product__atc-row');
    if (atcRow?.parentNode) {
      // Insert immediately after the atc-row — block sibling, not flex child
      atcRow.parentNode.insertBefore(el.urgency, atcRow.nextSibling);
    } else {
      // Fallback: append to the form
      const form = $qs('form.idea-product__form, form[action*="/cart/add"]');
      if (form) form.appendChild(el.urgency);
    }

    show(el.urgency);
  }
  /* ══════════════════════════════════════════════════════════════
     § 7  CART RESERVATION TIMER
     localStorage → survives page refreshes / soft navigations.
     ══════════════════════════════════════════════════════════════ */

  const LS_KEY = 'cart_reservation';
  const LEGACY_LS_KEY = 'cro_cart_reservation';
  let _timerInterval = null;

  function startReservationTimer(expiryTs) {
    if (!el.reservation || !el.resMsg || !el.resTimer) return;

    el.resMsg.textContent = T.reservation.msg;
    el.resTimer.setAttribute('aria-label', T.aria.reserveTimer);
    _injectReservationBanner();
    show(el.reservation);

    clearInterval(_timerInterval);

    const tick = () => {
      const remaining = expiryTs - Date.now();
      if (remaining <= 0) {
        clearInterval(_timerInterval);
        el.resTimer.textContent = T.reservation.label + '00:00';
        hide(el.reservation);
        localStorage.removeItem(LS_KEY);
        localStorage.removeItem(LEGACY_LS_KEY);
        return;
      }
      const totalSec = Math.floor(remaining / 1000);
      el.resTimer.textContent =
        T.reservation.label + pad2(Math.floor(totalSec / 60)) + ':' + pad2(totalSec % 60);
    };

    tick(); // render immediately — no blank first second
    _timerInterval = setInterval(tick, 1000);
  }

  function _injectReservationBanner() {
    // Ordered preference: cart drawer → cart page → product page fallback
    const targets = [
      '.idea-cart-drawer__footer',
      '.cart-drawer__footer',
      '.cart__footer',
      'form[action*="/cart"]',
    ];
    for (const sel of targets) {
      const target = $qs(sel);
      if (target) {
        target.insertBefore(el.reservation, target.firstChild);
        return;
      }
    }
    // Last resort — after urgency text on product page
    if (el.urgency?.parentNode) {
      el.urgency.parentNode.insertBefore(el.reservation, el.urgency.nextSibling);
    }
  }

  function _checkPersistedTimer() {
    const saved = localStorage.getItem(LS_KEY) || localStorage.getItem(LEGACY_LS_KEY);
    if (!saved) return;
    const expiry = parseInt(saved, 10);
    if (Number.isNaN(expiry) || Date.now() >= expiry) {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LEGACY_LS_KEY);
      return;
    }
    localStorage.setItem(LS_KEY, String(expiry));
    localStorage.removeItem(LEGACY_LS_KEY);
    startReservationTimer(expiry);
  }

  /* ══════════════════════════════════════════════════════════════
     § 8  CART ADD INTERCEPTOR
     BUG #4 FIX: no window.location manipulation at any point.
     BUG #7 FIX: reservation toast uses T dictionary.
     Three strategies to catch every theme's cart implementation.
     ══════════════════════════════════════════════════════════════ */

  let _fetchPatched = false;

  function interceptCartAdd() {
    _patchFetch();
    _patchXHR();
    _listenCustomEvents();
  }

  function _onCartAddSuccess() {
    const expiry = Date.now() + 60 * 60 * 1000; // 60 minutes
    localStorage.setItem(LS_KEY, String(expiry));
    localStorage.removeItem(LEGACY_LS_KEY);
    startReservationTimer(expiry);
  }

  // A) Patch window.fetch
  function _patchFetch() {
    if (_fetchPatched || typeof window.fetch !== 'function') return;
    _fetchPatched = true;
    const _native = window.fetch.bind(window);
    window.fetch  = function (resource, options) {
      const url = typeof resource === 'string'
        ? resource : (resource?.url ?? '');
      const promise = _native(resource, options);
      if (url.includes('/cart/add')) {
        promise.then((res) => { if (res.ok) _onCartAddSuccess(); }).catch(() => {});
      }
      return promise;
    };
  }

  // B) Patch XMLHttpRequest (older themes / jQuery AJAX)
  function _patchXHR() {
    if (typeof XMLHttpRequest === 'undefined') return;
    const _origOpen = XMLHttpRequest.prototype.open;
    const _origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._croUrl = typeof url === 'string' ? url : '';
      return _origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this._croUrl?.includes('/cart/add')) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) _onCartAddSuccess();
        }, { once: true });
      }
      return _origSend.apply(this, arguments);
    };
  }

  // C) Shopify ecosystem custom events
  function _listenCustomEvents() {
    ['cart:add', 'cart:item-added', 'product:added-to-cart']
      .forEach((name) => document.addEventListener(name, _onCartAddSuccess));
  }

  /* ══════════════════════════════════════════════════════════════
     § 9  SOCIAL PROOF TOASTS  (45–90 s random rotation)
     BUG #7 FIX: toast messages from T.toasts (bilingual).
     ══════════════════════════════════════════════════════════════ */

  let _dismissTimer   = null;
  let _scheduleTimer  = null;

  function _showToastMsg(message) {
    if (!el.toast) return;
    clearTimeout(_dismissTimer);
    el.toast.classList.remove('cro-toast--in', 'cro-toast--out');
    el.toast.textContent = message;
    show(el.toast);
    void el.toast.offsetWidth; // force reflow so animation restarts
    el.toast.classList.add('cro-toast--in');

    _dismissTimer = setTimeout(() => {
      el.toast.classList.replace('cro-toast--in', 'cro-toast--out');
      setTimeout(() => {
        hide(el.toast);
        el.toast.classList.remove('cro-toast--out');
      }, 350);
    }, 4000);
  }

  function _scheduleNextToast() {
    const delaySec = 45 + Math.random() * 45;
    _scheduleTimer = setTimeout(() => {
      _showToastMsg(T.toasts[Math.floor(Math.random() * T.toasts.length)]);
      _scheduleNextToast();
    }, delaySec * 1000);
  }

  /* ══════════════════════════════════════════════════════════════
     § 10  INIT
     BUG #6 FIX: ALL components activate immediately on DOMContentLoaded.
     ══════════════════════════════════════════════════════════════ */

  function init() {
    // Inline elements (order matters for DOM position)
    initStock();         // before atc-row
    initUrgency();       // after atc-row
    // State restoration
    _checkPersistedTimer();
    // Event interception
    interceptCartAdd();
    // Social proof loop
    _scheduleNextToast();
  }

  // Script is defer-loaded; DOM is ready by execution time.
  // Guard for edge cases where defer is not respected.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(); // end CROController
